/**
 * Sticky chat panel pinned bottom-right of the page-body container via a 0-height sticky
 * wrapper Box + an absolutely-positioned Paper child.
 *
 * Architecture:
 * - A `<Box sx={{ position: "sticky", bottom: 16, height: 0, ... }}>` wrapper acts as the
 *   positional placeholder. Its height is 0, so it contributes nothing to the document flow
 *   and the footer is never pushed down — even when the panel is expanded.
 * - The `<Paper sx={{ position: "absolute", bottom: 0, right: 0, width: "100%" }}>` sits
 *   inside this wrapper. Because `bottom: 0` anchors the Paper to the bottom of the wrapper,
 *   and the wrapper itself is sticky at `bottom: 16`, the Paper's lower edge is always at
 *   `bottom: 16` of the scroll viewport. When the body is expanded, the Paper grows upward
 *   (its `bottom` is fixed, its top moves up).
 * - DOM order inside the Paper: Header first, then Body. This is the natural visual order
 *   (Header on top, Body below) and matches the upward-growth direction correctly.
 * - Body is wrapped in `<Collapse in={isOpen} timeout="auto">`. When collapsed, Collapse
 *   animates the body height to 0, so the Paper shrinks to header height only. This works
 *   correctly because the body is in normal document flow (not absolutely positioned).
 *
 * Collapse mechanics:
 * - `Collapse` measures `scrollHeight` of its child wrapper and animates the height between
 *   0 and the natural body height. Since the body is in normal document flow, `scrollHeight`
 *   is correctly non-zero and the animation works as expected.
 * - The chevron icon points down when open (`rotate(180deg)`) and up when closed (`none`),
 *   matching the upward growth direction of the panel.
 *
 * Open/closed state persisted in localStorage via `panelStorage`. No FAB.
 *
 * Selected-chat semantics: on mount and whenever `requestId` changes, the SYSTEM chat is
 * auto-selected. A manual 1:1 selection survives collapse/expand cycles but is reset on
 * requestId change or page reload — no localStorage persistence for the selected chat id.
 *
 * Conversation data ownership: this component is presentational with respect to the
 * conversation — `conversation`/`isLoading`/`sendMessage`/`markRead` are received as props,
 * and `onRefresh` re-triggers the load owned by the parent. The single `useConversation` call
 * lives in the `NotificationsWithPanel` wrapper, which is also the single place deciding
 * whether the panel (and its layout spacer) should be visible at all — see
 * `NotificationsWithPanel.tsx`.
 */

import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Collapse, Divider, IconButton, Paper, Tooltip, Typography} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {Refresh as RefreshIcon} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {CommonI18N, NotificationI18N} from "@/translations";
import type {UseConversationResult} from "./state/useConversation";
import {type ChatDTO, ChatType} from "./types";
import ChatList from "./ChatList";
import ChatWindow from "./ChatWindow";
import {readPanelOpenState, writePanelOpenState} from "./panelStorage";
import {PANEL_BOTTOM_OFFSET_PX} from "./PanelReservedSpace";

interface NotificationsPanelProps
    extends Pick<UseConversationResult, "conversation" | "isLoading" | "sendMessage" | "markRead"> {
    requestId: number;
    /** Re-triggers the conversation load; owned by the parent `useConversation` call. */
    onRefresh: () => void;
    /**
     * Ref attached to the always-visible header row. `NotificationsWithPanel` measures its
     * height to size the layout spacer exactly (the collapsed panel footprint), so the reserved
     * scroll space always matches the real header height regardless of theme/font.
     */
    headerRef?: React.Ref<HTMLDivElement>;
}

/**
 * Sticky collapsible chat panel pinned to the bottom-right of the page content area.
 *
 * Uses a 0-height sticky wrapper Box as positional placeholder and an absolutely-positioned
 * Paper child that grows upward when the body is expanded. Footer is never displaced because
 * the wrapper contributes zero height to the document flow.
 *
 * DOM order inside the Paper: Header first (top), Body second (bottom). When collapsed, Collapse
 * animates the body height to 0 so only the header is visible; when expanded, the full panel
 * height grows upward above the header.
 *
 * Open/close state is persisted in localStorage via `panelStorage`
 * (key `boxhub.notifications.panel.open`). No FAB.
 *
 * Visibility is decided entirely by the parent `NotificationsWithPanel` wrapper — this
 * component is only ever rendered once the wrapper has determined the panel has visible
 * content, so it renders unconditionally (no internal loading/empty early-returns).
 */
const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
    requestId,
    conversation,
    isLoading,
    sendMessage,
    markRead,
    onRefresh,
    headerRef
}) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState<boolean>(() => readPanelOpenState());
    const [selectedChatId, setSelectedChatId] = useState<number | undefined>(undefined);

    const selectedChat = useMemo(
        () => conversation?.chats?.find((c) => c.id === selectedChatId),
        [conversation, selectedChatId]
    );

    // Reset selected chat when requestId changes so that a 1:1 selection from a previous
    // request does not carry over to the newly loaded conversation.
    useEffect(() => {
        setSelectedChatId(undefined);
    }, [requestId]);

    // Auto-select the SYSTEM chat once the conversation for the current requestId is loaded
    // and no chat has been selected yet. The gate `conversation?.requestId === requestId`
    // prevents a flash of the SYSTEM chat from the previous (still in-flight) conversation.
    // The guard `selectedChatId === undefined` makes this effect idempotent — a manual
    // 1:1 selection by the user is never overwritten.
    useEffect(() => {
        if (conversation?.requestId !== requestId || selectedChatId !== undefined) {
            return;
        }
        const systemChat = conversation.chats?.find((c) => c.type === ChatType.SYSTEM);
        if (systemChat?.id !== undefined) {
            setSelectedChatId(systemChat.id);
        }
    }, [conversation, requestId, selectedChatId]);

    const handleRefresh = useCallback(
            (event: React.MouseEvent) => {
                event.stopPropagation();
                onRefresh();
        },
            [onRefresh]
    );

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => {
            const next = !prev;
            writePanelOpenState(next);
            return next;
        });
    }, []);

    const handleSelectChat = useCallback((chat: ChatDTO) => {
        if (chat.id !== undefined) {
            setSelectedChatId(chat.id);
        }
    }, []);

    const totalUnread = useMemo(
            () => conversation?.chats?.reduce((sum, c) => sum + Math.max(0, c.unreadCount ?? 0), 0) ?? 0,
            [conversation?.chats]
    );

    return (
            // 0-height sticky wrapper — acts as the positional anchor at bottom: 16 of the
            // scroll viewport. height: 0 means this node contributes nothing to document flow,
            // so the footer is never pushed down regardless of the panel's expanded height.
            <Box
                    sx={{
                        position: "sticky",
                        bottom: PANEL_BOTTOM_OFFSET_PX,
                        height: 0,
                        marginLeft: "auto",
                        width: "60%",
                        maxWidth: 700,
                        minWidth: 320,
                        // Keep zIndex below MUI modals (1300) so dialogs correctly overlay the panel.
                        // 1100 = AppBar level in MUI default z-index scale.
                        zIndex: 1100
                    }}
            >
                {/* Absolutely-positioned Paper — anchored to bottom of the wrapper (= sticky
                position). Grows upward when body is expanded because `bottom: 0` is fixed
                and the paper's top edge moves up as content is added. */}
                <Paper
                        elevation={6}
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            width: "100%",
                            borderRadius: 2,
                            overflow: "hidden"
                        }}
                >
                    {/* Panel header — always visible, click to toggle collapse.
                    DOM order: Header first (top), Body below — matches natural visual order
                    and the upward-growth direction of the panel. */}
                    <Box
                            ref={headerRef}
                            onClick={toggleOpen}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                cursor: "pointer",
                                userSelect: "none"
                            }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <ChatBubbleOutlineIcon fontSize="small"/>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {t(NotificationI18N.chat.panel.title)}
                            </Typography>
                            {totalUnread > 0 && (
                                    <Box
                                            sx={{
                                                bgcolor: "error.main",
                                                color: "error.contrastText",
                                                borderRadius: "50%",
                                                minWidth: 20,
                                                height: 20,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 11,
                                                fontWeight: "bold",
                                                px: 0.5
                                            }}
                                    >
                                        {totalUnread}
                                    </Box>
                            )}
                        </Box>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <Tooltip title={t(CommonI18N.action.refresh)}>
                                <IconButton
                                        size="small"
                                        sx={{color: "inherit"}}
                                        disabled={isLoading}
                                        onClick={handleRefresh}
                                >
                                    <RefreshIcon/>
                                </IconButton>
                            </Tooltip>
                            {/* Chevron points down when open (rotate 180°) — indicates "click to
                            collapse downward". Points up when closed — indicates "click to
                            expand upward". This matches the panel's upward-growth direction. */}
                            <Tooltip
                                    title={isOpen ? t(NotificationI18N.chat.panel.collapse) : t(NotificationI18N.chat.panel.expand)}
                            >
                                <IconButton
                                        size="small"
                                        sx={{
                                            color: "inherit",
                                            transform: isOpen ? "rotate(180deg)" : "none",
                                            transition: "transform 0.2s"
                                        }}
                                >
                                    <ExpandLessIcon/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Panel body — rendered below the header in DOM order. Collapse animates
                    height to 0 when isOpen is false, so the Paper shrinks to header height.
                    This works because the body is in normal document flow, giving Collapse
                    a non-zero scrollHeight to animate against. */}
                    <Collapse in={isOpen} timeout="auto">
                        <Box>
                            <Divider/>
                            {/* Body content */}
                            <Box sx={{height: 480, display: "flex", flexDirection: "row"}}>
                            {/* Left column: chat list */}
                            <Box
                                    sx={{
                                        flex: "0 0 30%",
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "100%",
                                        overflowY: "auto",
                                        borderRight: "1px solid",
                                        borderColor: "divider"
                                    }}
                            >
                                <ChatList
                                        conversation={conversation}
                                        onSelectChat={handleSelectChat}
                                        selectedChatId={selectedChatId}
                                />
                            </Box>

                            {/* Right column: chat window or empty state */}
                            <Box
                                    sx={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "100%",
                                        overflowY: "auto"
                                    }}
                            >
                                {selectedChat ? (
                                        <ChatWindow
                                                requestId={requestId}
                                                chat={selectedChat}
                                                onSendMessage={sendMessage}
                                                onMarkRead={markRead}
                                        />
                                ) : (
                                        <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flex: 1,
                                                    color: "text.secondary",
                                                    p: 2
                                                }}
                                        >
                                            <Typography variant="body2">
                                                {t(NotificationI18N.chat.window.noSelection)}
                                            </Typography>
                                        </Box>
                                )}
                            </Box>
                        </Box>
                        </Box>
                    </Collapse>
                </Paper>
            </Box>
    );
};

export default NotificationsPanel;
